import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { Edit2, DocumentText1 } from "iconsax-react";
import { FileUploader } from "@/shared/components";
import { ModalForm, Button, Input, DatePicker, Toast, Prompt } from "@/shared/ui";
import { extractErrorMessage, formatDateToISO, trimMoneyDecimals, type Locale } from "@/shared/utils";
import { createEmployeeAdvanceSchema, type EmployeeAdvanceFormValues } from "../validation/advanceValidation";
import { useResubmitSelfAdvanceMutation } from "../api";
import type { SelfServiceRequest, AdvanceAttachment } from "../types";

interface Props {
  request: SelfServiceRequest;
  onClose: () => void;
  onSuccess: () => void;
}

export default function EditSelfAdvanceForm({ request, onClose, onSuccess }: Props) {
  const { t, i18n } = useTranslation("EmployeePortal");
  const locale = (i18n.language as Locale) || "ru";

  const details = request.details ?? {};
  const initialAttachments: AdvanceAttachment[] = details.attachments ?? [];

  const [prompt, setPrompt] = useState<{ title: string; text: string; variant?: "success" | "error" } | null>(null);
  const [existingAttachments, setExistingAttachments] = useState<AdvanceAttachment[]>(initialAttachments);
  const [removedAttachmentIds, setRemovedAttachmentIds] = useState<number[]>([]);
  const [newFiles, setNewFiles] = useState<File[]>([]);

  const [resubmitAdvance, { isLoading }] = useResubmitSelfAdvanceMutation();

  const {
    register,
    watch,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<EmployeeAdvanceFormValues>({
    resolver: zodResolver(createEmployeeAdvanceSchema),
    defaultValues: {
      amount: trimMoneyDecimals(details.amount),
      document_number: details.document_number ?? "",
      document_date: details.document_date ? new Date(String(details.document_date) + "T00:00:00") : null,
    },
  });

  function handleRemoveExisting(id: number) {
    setExistingAttachments((prev) => prev.filter((file) => file.id !== id));
    setRemovedAttachmentIds((prev) => [...prev, id]);
  }

  function handleAddFile(file: File | null) {
    if (!file) return;
    setNewFiles((prev) => [...prev, file]);
  }

  function handleRemoveNewFile(index: number) {
    setNewFiles((prev) => prev.filter((_, i) => i !== index));
  }

  async function onSubmit(data: EmployeeAdvanceFormValues) {
    if (isLoading) return;
    try {
      const formData = new FormData();
      formData.append("amount", String(Number(data.amount)));
      formData.append("document_number", data.document_number);
      if (data.document_date) {
        formData.append("document_date", formatDateToISO(data.document_date));
      }
      removedAttachmentIds.forEach((id) => formData.append("remove_attachment_ids", String(id)));
      newFiles.forEach((file) => formData.append("files", file));

      await resubmitAdvance({ id: request.id, body: formData }).unwrap();
      setPrompt({
        title: t("advance.resubmit.successTitle"),
        text: t("advance.resubmit.successText"),
        variant: "success",
      });
    } catch (err: unknown) {
      setPrompt({
        title: t("advance.resubmit.errorTitle"),
        text: extractErrorMessage(err, "advance.resubmit.errorText", t),
        variant: "error",
      });
    }
  }

  function handlePromptClose() {
    const wasSuccess = prompt?.variant === "success";
    setPrompt(null);
    if (wasSuccess) {
      onSuccess();
    }
  }

  if (prompt) {
    return (
      <Prompt
        variant={prompt.variant || "success"}
        onClose={handlePromptClose}
        title={prompt.title}
        text={prompt.text}
        namespace="EmployeePortal"
      />
    );
  }

  return (
    <ModalForm
      icon={Edit2}
      onClose={onClose}
      footer={
        <div className="grid grid-cols-[2fr_3fr] gap-3">
          <Button variant="secondary" size="huge" onClick={onClose} type="button">
            {t("advance.buttons.cancel")}
          </Button>
          <Button variant="primary" size="huge" type="submit" form="edit-self-advance-form" disabled={isLoading}>
            {isLoading ? t("advance.buttons.loading") : t("advance.resubmit.submit")}
          </Button>
        </div>
      }>
      <form id="edit-self-advance-form" className="flex flex-col h-full" onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-2 shrink-0 pb-5 border-b surface-base-stroke">
          <h4 className="text-display-2xs content-base-primary">{t("advance.resubmit.title")}</h4>
        </div>

        <div className="flex-1 overflow-auto flex flex-col gap-5 py-5 px-1 page-scroll">
          {request.application_review_note && (
            <Toast
              color="notice"
              text={`${t("leaveForm.revisionNotePrefix")}: ${request.application_review_note}`}
              closable={false}
              autoClose={false}
              isFullWidth
            />
          )}

          <Input
            label={t("advance.fields.amount")}
            hasRequiredLabel
            placeholder="0"
            onlyNumber
            {...register("amount")}
            error={errors.amount?.message ? t(errors.amount.message) : undefined}
          />

          <Input
            label={t("advance.fields.documentNumber")}
            hasRequiredLabel
            placeholder={t("advance.fields.documentNumberPlaceholder")}
            {...register("document_number")}
            error={errors.document_number?.message ? t(errors.document_number.message) : undefined}
          />

          <DatePicker
            locale={locale}
            mode="single"
            label={t("advance.fields.documentDate")}
            hasRequiredLabel
            value={watch("document_date") ?? null}
            onChange={(v) => setValue("document_date", v as Date | null, { shouldValidate: true })}
            error={errors.document_date?.message ? t(errors.document_date.message as string) : undefined}
          />

          <div className="flex flex-col gap-3">
            <p className="text-body-bold-sm content-base-primary">{t("advance.fields.attachments")}</p>

            {existingAttachments.length > 0 && (
              <div className="flex flex-col gap-2">
                {existingAttachments.map(function renderExistingFile(file) {
                  return (
                    <div
                      key={file.id}
                      className="surface-tertiary-fill rounded-[10px] p-0.5 input-box-shadow flex items-center">
                      <a
                        href={file.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex flex-1 items-center gap-2 radius-xs surface-base-fill py-3 px-2 min-w-0">
                        <span className="content-base-low shrink-0">
                          <DocumentText1 size={16} color="currentColor" />
                        </span>
                        <p className="content-base-primary text-label-sm truncate min-w-0 flex-1">{file.filename}</p>
                      </a>
                      <button
                        type="button"
                        onClick={() => handleRemoveExisting(file.id)}
                        className="w-10 aspect-square flex items-center justify-center content-action-neutral hover:opacity-80 transition-opacity duration-150 shrink-0">
                        <span className="text-sm">×</span>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {newFiles.length > 0 && (
              <div className="flex flex-col gap-2">
                {newFiles.map(function renderNewFile(file, index) {
                  return (
                    <div
                      key={index}
                      className="surface-tertiary-fill rounded-[10px] p-0.5 input-box-shadow flex items-center">
                      <div className="flex flex-1 items-center gap-2 radius-xs surface-base-fill py-3 px-2 min-w-0">
                        <span className="content-base-low shrink-0">
                          <DocumentText1 size={16} color="currentColor" />
                        </span>
                        <p className="content-base-primary text-label-sm truncate min-w-0 flex-1">{file.name}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveNewFile(index)}
                        className="w-10 aspect-square flex items-center justify-center content-action-neutral hover:opacity-80 transition-opacity duration-150 shrink-0">
                        <span className="text-sm">×</span>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            <FileUploader
              label={t("advance.buttons.addFile")}
              accept=".pdf"
              maxSizeMB={10}
              value={null}
              onChange={handleAddFile}
            />
          </div>
        </div>
      </form>
    </ModalForm>
  );
}
